// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * The running account of what unfence did.
 *
 * It earns its place on a tool that writes to disk: "backup verified, 4 files,
 * 18,204 bytes" followed by "wrote src/app.ts (1,204 bytes)" is the difference
 * between trusting the tool and hoping. It is also the only production log —
 * `console.*` is dropped from the build entirely, because a stray debug line
 * would print somebody's source code into a console a screen share picks up.
 */

import type { EventEntry } from './types';
import { makeOverlay, type Overlay } from './overlays';

const MAX_ENTRIES = 500;

export class EventLog {
  private entries: EventEntry[] = [];
  private list: HTMLElement;
  private counter: HTMLElement | null;
  private overlay: Overlay;

  constructor(root: HTMLElement, counter: HTMLElement | null) {
    this.list = root.querySelector('[data-log-list]') as HTMLElement;
    this.counter = counter;
    // The drawer is an overlay like any other: on a phone it covers the button
    // that opened it, so a close control, an outside tap and Escape all have to
    // work or the tool is simply stuck.
    this.overlay = makeOverlay(root);
  }

  add(level: EventEntry['level'], text: string): void {
    const at = new Date().toLocaleTimeString([], { hour12: false });
    this.entries.push({ at, level, text });
    if (this.entries.length > MAX_ENTRIES) this.entries.shift();

    const row = document.createElement('div');
    row.className = `log-row log-${level}`;
    const time = document.createElement('span');
    time.className = 'log-time';
    time.textContent = at;
    const body = document.createElement('span');
    body.className = 'log-text';
    // textContent, never innerHTML: file paths and error messages are
    // third-party content and this is the one place they all funnel through.
    body.textContent = text;
    row.append(time, body);
    this.list.appendChild(row);
    this.list.scrollTop = this.list.scrollHeight;

    if (this.counter) this.counter.textContent = String(this.entries.length);
  }

  info(text: string): void { this.add('info', text); }
  good(text: string): void { this.add('good', text); }
  warn(text: string): void { this.add('warn', text); }
  bad(text: string): void { this.add('bad', text); }

  toggle(): void {
    if (this.overlay.isOpen) this.overlay.close();
    else this.overlay.open();
  }

  get text(): string {
    return this.entries.map((e) => `[${e.at}] ${e.text}`).join('\n');
  }

  clear(): void {
    this.entries = [];
    this.list.replaceChildren();
    if (this.counter) this.counter.textContent = '0';
  }
}
